/**
 * /tracking/macros — full macro library reference + playground (feature #175).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `MacroLibraryPage`. The flat-file naming convention uses dots as path
 * separators, so `tracking.macros.tsx` maps to `/tracking/macros`.
 *
 * Search params:
 *   ?category=click|campaign|...   Pre-filter the reference to one category.
 *   ?template=<url>                Pre-fill the playground template.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import {
	MACRO_CATEGORIES,
	type MacroCategory,
} from "@/features/macro-library/api";
import { MacroLibraryPage } from "@/features/macro-library/MacroLibraryPage";

// ---------------------------------------------------------------------------
// Search coercion
// ---------------------------------------------------------------------------

interface TrackingMacrosSearch {
	category?: MacroCategory;
	template?: string;
}

function coerceCategory(raw: unknown): MacroCategory | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim().toLowerCase();
	if ((MACRO_CATEGORIES as readonly string[]).includes(trimmed)) {
		return trimmed as MacroCategory;
	}
	return undefined;
}

function coerceTemplate(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim();
	if (!trimmed || trimmed.length > 4096) return undefined;
	return trimmed;
}

// ---------------------------------------------------------------------------
// Route — mounts at /tracking/macros
// ---------------------------------------------------------------------------

export const trackingMacrosRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/macros",
	validateSearch: (raw: Record<string, unknown>): TrackingMacrosSearch => {
		const category = coerceCategory(raw.category);
		const template = coerceTemplate(raw.template);
		const out: TrackingMacrosSearch = {};
		if (category) out.category = category;
		if (template) out.template = template;
		return out;
	},
	component: TrackingMacrosRoute,
});

export default trackingMacrosRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + feature component
// ---------------------------------------------------------------------------

export function TrackingMacrosRoute() {
	const { category, template } = trackingMacrosRoute.useSearch();
	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<MacroLibraryPage
				initialCategory={category}
				initialTemplate={template}
			/>
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Macro library</li>
			</ol>
		</nav>
	);
}

export { MacroLibraryPage } from "@/features/macro-library/MacroLibraryPage";
